import {ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params, RouterLink} from '@angular/router';
import {Task} from 'src/app/shared/models/task.model';
import {DayInformationService} from 'src/app/services/day-information.service';
import {Occasion} from 'src/app/shared/models/occasion.model';
import {DatePipe, formatDate, Location, NgClass} from '@angular/common';
import {isSameDayOccasion} from 'src/app/core/utils/date.utils';
import {DayInformation} from 'src/app/shared/models/day-information.model';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {RecommendationFilterComponent} from '../recommendation-filter/recommendation-filter.component';
import {StorageService} from 'src/app/services/storage.service';
import {Weight} from 'src/app/shared/models/weight.model';
import {TitleService} from 'src/app/services/title.service';
import {MetaService} from 'src/app/services/meta.service';
import {MatDialog} from '@angular/material/dialog';
import {MatCard, MatCardContent} from "@angular/material/card";
import {DateWeekdayShortPipe} from "src/app/core/utils/pipe.utils";
import {routeDateFormat} from "src/app/core/constants/route.formats";
import {LanguageService} from 'src/app/services/language.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-recommendation-details',
    templateUrl: './recommendation-details.component.html',
    imports: [
        TranslatePipe,
        RouterLink,
        MatCard,
        MatCardContent,
        DateWeekdayShortPipe,
        NgClass,
        DatePipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./recommendation-details.component.scss']
})
export class RecommendationDetailsComponent implements OnInit {

    @ViewChild('filterBtnRef', {static: false}) public filterBtnRef: ElementRef | undefined;

    private readonly destroyRef = inject(DestroyRef);

    currentDay: Date = new Date();
    dayInformation: DayInformation | undefined;
    task: Task | undefined;
    occasions: Occasion[] | undefined;
    todayOccasion: Occasion | undefined;

    constructor(
        private route: ActivatedRoute,
        private dayService: DayInformationService,
        private location: Location,
        private translate: TranslateService,
        private titleService: TitleService,
        private metaService: MetaService,
        private dialog: MatDialog,
        private storage: StorageService,
        public language: LanguageService
    ) {
    }

    ngOnInit(): void {
        this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            this.initTask(params);
        });

        this.dayService.getDayInformation(this.currentDay.getTime()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((dayInformation: DayInformation) => {
            this.dayInformation = dayInformation;
        });

        this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
            this.dayService.initTasks();
            this.refreshTasks();
        })
    }

    initTask(params: Params) {
        const taskId = params['taskId'];
        if (!taskId) {
            return;
        }

        this.dayService.getAllTasks().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tasks: Task[]) => {
            this.task = tasks.find(task => task.id === taskId);

            if (!this.task) {
                return
            }

            this.titleService.updateTitleText(this.translate, this.task.header);
            this.metaService.updateDescription(this.task.description);
            this.metaService.updateCanonicalUrl(`/details/${this.task.id}`);
            this.metaService.updateSocialTags(
                this.task.header,
                this.task.description,
                `/details/${this.task.id}`
            );

            this.dayService.getNextOccasions(taskId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((occasions: Occasion[]) => {
                this.todayOccasion = occasions.find(occasion => isSameDayOccasion(occasion.date, this.currentDay));
                this.occasions = this.filterOccasions(occasions);

                if (this.todayOccasion) {
                    const index = this.occasions.indexOf(this.todayOccasion, 0);
                    this.occasions.splice(index, 1);
                }
            });
        });
    }

    formatDateForRouter(date: number) {
        return formatDate(date, routeDateFormat, 'en');
    }

    navigateBack() {
        this.location.back();
    }

    openFilterDialog() {
        if (!this.filterBtnRef) {
            return;
        }

        const dialogRef = this.dialog.open(RecommendationFilterComponent, {
            width: 'min(420px, 92vw)',
            maxWidth: '92vw'
        });

        dialogRef.afterClosed().subscribe(_ => {
            this.refreshTasks();
        });
    }

    refreshTasks() {
        this.initTask(this.route.snapshot.params);
    }

    private filterOccasions(occasions: Occasion[]) {
        const showOnlyGood = this.storage.getShowOnlyGood();
        if (!showOnlyGood) {
            return occasions;
        }

        return occasions.filter(occasion => occasion.weight === Weight.best || occasion.weight === Weight.good);
    }
}
